"use client";

import { useMemo, useRef, useState } from "react";
import {
  Field,
  NumberInput,
  SelectInput,
  PrintButton,
  CopyButton,
  DataReviewedNote,
  PrintFooter,
  trackToolResult,
} from "@/components/tools/ui";

/* ── Site wind limits reference ────────────────────────────────────────────
   These are commonly used planning figures. The manufacturer's handbook, the
   appointed person (for lifts) and the temporary works designer always govern,
   and GUSTS matter more than the mean — a gust can exceed the mean by half
   again. Verified figures:
   - MEWPs: 12.5 m/s (28 mph), Beaufort 6, per IPAF / most machines; some are
     rated lower — read the data plate / handbook. (IPAF; HSE MEWP topic.)
   - Scaffold work at height: commonly stop active work ~17 mph (Beaufort 5),
     and scaffolds are generally stable in winds up to ~25 mph if properly
     erected and tied. (NASC guidance; industry practice.)
   - Tower scaffolds (PASMA): cease work and reduce height at 17 mph (Beaufort
     4) mean; tie in to a rigid structure if 25 mph is likely; dismantle if
     40 mph is likely. (PASMA.)
   - Roof sheeting / fragile-roof work: handling large sheets becomes dangerous
     well below general limits — commonly stop sheet handling ~17 mph, and the
     NFRC recommends roofing work cease by ~23 mph (10.3 m/s). (NFRC.)
   - Crane lifting: many cranes quote ~9.8 m/s in-service wind, but the lift
     plan / manufacturer's chart governs and large-area loads derate it
     sharply. All lifting typically ceases around 38-40 mph. (HSE L113 LOLER;
     manufacturer charts; lift plan.)
   - Ladder / step work and hoardings: judgement-based — leave ladders in
     gusty conditions, and inspect hoardings and temporary works after any
     gale. (HSE.)
   Beaufort sight cues are from the standard Beaufort scale. */

interface Activity {
  key: string;
  name: string;
  /** Stop-work mean wind in m/s. */
  stopMs: number;
  /** A lower caution threshold in m/s where extra care / monitoring starts. */
  cautionMs?: number;
  /** Beaufort number at the stop threshold. */
  beaufort: number;
  /** Who/what governs the actual limit. */
  governs: string;
  /** What to do at the limit. */
  action: string;
  /** Any notes — e.g. tie-in / dismantle steps. */
  note?: string;
}

const ACTIVITIES: Activity[] = [
  {
    key: "mewp",
    name: "MEWP / cherry picker (boom or scissor)",
    stopMs: 12.5,
    cautionMs: 10,
    beaufort: 6,
    governs: "the machine's data plate and operator handbook",
    action:
      "Stop and bring the platform down. 12.5 m/s (28 mph) is the common maximum, but many machines are rated lower — read the data plate.",
    note: "Large boards or sheets held at height create a sail and lower the safe limit further.",
  },
  {
    key: "scaffold",
    name: "Scaffold work at height (tube & fitting / system)",
    stopMs: 7.6, // ~17 mph
    beaufort: 5,
    governs: "the scaffold design and the site temporary works arrangements",
    action:
      "Stop active work at height around 17 mph. A properly erected and tied scaffold is generally stable in winds up to about 25 mph, but the people on it are not.",
    note: "Inspect the scaffold and ties after any gale before it is used again.",
  },
  {
    key: "tower",
    name: "Mobile tower scaffold (PASMA)",
    stopMs: 7.6, // 17 mph mean
    beaufort: 4,
    governs: "the tower instructions and PASMA guidance",
    action:
      "Cease work and reduce the tower height at a 17 mph mean wind. Tie the tower to a rigid structure if 25 mph is likely; dismantle it if 40 mph is likely.",
    note: "Never move a tower in strong or gusty wind, and never sheet a tower unless it is designed for it.",
  },
  {
    key: "roof-sheeting",
    name: "Roof sheeting / cladding / fragile-roof work",
    stopMs: 7.6, // ~17 mph for handling sheets
    cautionMs: 6,
    beaufort: 5,
    governs: "the manufacturer's handling guidance and the roof work method statement",
    action:
      "Stop handling large sheets and cladding panels around 17 mph — a sheet acts like a sail and can pull a worker off balance. The NFRC recommends roofing work cease by about 23 mph.",
    note: "Fragile-roof and edge risks make this the lowest practical limit on site — gusts decide it.",
  },
  {
    key: "crane",
    name: "Crane lifting / lifting operations",
    stopMs: 9.8,
    cautionMs: 8,
    beaufort: 5,
    governs: "the lift plan, the appointed person and the crane's wind chart",
    action:
      "Many cranes quote about 9.8 m/s (22 mph) in-service wind, but the lift plan and the crane's load/wind chart govern — and large-area loads derate it sharply. Stop the lift if the limit in the plan is reached.",
    note: "All lifting typically stops by 38-40 mph regardless. Confirm load weight, rigging and exclusion zone before every lift.",
  },
  {
    key: "ladder",
    name: "Ladder / step ladder work",
    stopMs: 9, // ~20 mph judgement
    beaufort: 5,
    governs: "the user's judgement and the task risk assessment",
    action:
      "Avoid ladder work in strong or gusty wind — by around 20 mph a ladder is hard to control. Use a more stable means of access, or wait for the wind to drop.",
  },
  {
    key: "hoarding",
    name: "Hoarding / temporary works (inspect after gales)",
    stopMs: 17, // ~38 mph gale
    beaufort: 8,
    governs: "the temporary works designer and the inspection regime",
    action:
      "Hoardings and temporary works are designed to a wind load, but a gale (around Beaufort 8, ~38 mph) is the trigger to inspect them afterwards for movement, damage or loosened fixings before the area is reopened.",
    note: "Loose materials, scaffold sheeting and stacked panels become missiles in a gale — secure or remove them.",
  },
];

/* Beaufort sight cues — what a foreman sees, so a judgement call is possible
   without an anemometer. Standard Beaufort scale descriptions. */
interface Beaufort {
  n: number;
  name: string;
  ms: string; // mean range m/s
  mph: string;
  cue: string;
}

const BEAUFORT: Beaufort[] = [
  { n: 3, name: "Gentle breeze", ms: "3.4–5.4", mph: "8–12", cue: "Leaves and twigs in constant motion; a light flag extends." },
  { n: 4, name: "Moderate breeze", ms: "5.5–7.9", mph: "13–18", cue: "Dust and loose paper lift; small branches move." },
  { n: 5, name: "Fresh breeze", ms: "8.0–10.7", mph: "19–24", cue: "Small leafy trees sway; crests form on water; you feel it pushing you." },
  { n: 6, name: "Strong breeze", ms: "10.8–13.8", mph: "25–31", cue: "Large branches sway; umbrellas hard to use; wires whistle." },
  { n: 7, name: "Near gale", ms: "13.9–17.1", mph: "32–38", cue: "Whole trees in motion; effort needed to walk into the wind." },
  { n: 8, name: "Gale", ms: "17.2–20.7", mph: "39–46", cue: "Twigs break off trees; very hard to walk; loose materials become missiles." },
];

const msToMph = (ms: number) => Math.round(ms * 2.23694);

export function WindLimitsChecker() {
  const [forecast, setForecast] = useState("");
  const [unit, setUnit] = useState<"mph" | "ms">("mph");
  const tracked = useRef(false);

  // Forecast wind expressed in m/s for comparison.
  const forecastMs = useMemo(() => {
    const v = parseFloat(forecast);
    if (!isFinite(v) || v <= 0) return null;
    return unit === "mph" ? v / 2.23694 : v;
  }, [forecast, unit]);

  // Mark each activity stopped if the forecast meets or exceeds its stop limit.
  const checked = useMemo(() => {
    return ACTIVITIES.map((a) => {
      const stop = forecastMs !== null && forecastMs >= a.stopMs;
      const caution =
        forecastMs !== null && !stop && a.cautionMs !== undefined && forecastMs >= a.cautionMs;
      return { activity: a, stop, caution };
    });
  }, [forecastMs]);

  const stoppedCount = checked.filter((c) => c.stop).length;

  if (forecastMs !== null && !tracked.current) {
    tracked.current = true;
    trackToolResult("wind-limits-checker", { forecastMph: msToMph(forecastMs), stopped: stoppedCount });
  }

  const buildCopyText = () => {
    const lines: string[] = [];
    lines.push("SITE WIND ACTION CARD");
    lines.push("Commonly used planning limits — the manufacturer's handbook, appointed person or temporary works designer governs. Gusts matter more than the mean.");
    lines.push("");
    ACTIVITIES.forEach((a) => {
      lines.push(`${a.name}: stop at ${a.stopMs} m/s (${msToMph(a.stopMs)} mph, Beaufort ${a.beaufort}).`);
      lines.push(`  ${a.action}`);
    });
    lines.push("");
    lines.push("Sight cues (Beaufort): B5 fresh breeze ~19-24 mph, trees sway, you feel it push you; B6 strong breeze ~25-31 mph, large branches sway, umbrellas hard to use.");
    return lines.join("\n");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Reference table + forecast input */}
      <div className="space-y-6">
        <div className="card shadow-soft p-6 print-hide">
          <h2 className="text-xl">Today&apos;s wind (optional)</h2>
          <p className="mt-1 text-sm text-muted">
            Enter the forecast or measured wind to highlight which activities should stop. Use the mean from a
            handheld anemometer where you can — and remember a gust can be half again as strong.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_140px]">
            <Field label="Wind speed">
              <NumberInput
                min={0}
                step={1}
                value={forecast}
                onChange={(e) => setForecast(e.target.value)}
                placeholder="e.g. 22"
              />
            </Field>
            <Field label="Unit">
              <SelectInput value={unit} onChange={(e) => setUnit(e.target.value as "mph" | "ms")}>
                <option value="mph">mph</option>
                <option value="ms">m/s</option>
              </SelectInput>
            </Field>
          </div>
          {forecastMs !== null && (
            <p className="mt-3 rounded-lg bg-mist px-3 py-2 text-[0.85rem] leading-relaxed text-body" aria-live="polite">
              At <strong className="text-ink">{msToMph(forecastMs)} mph ({forecastMs.toFixed(1)} m/s)</strong>,{" "}
              {stoppedCount === 0 ? (
                <>no activities below have hit their stop limit — but check gusts and watch for rising wind.</>
              ) : (
                <>
                  <strong className="text-risk-veryhigh">{stoppedCount}</strong> of {ACTIVITIES.length}{" "}
                  activities below are at or over their stop limit (highlighted).
                </>
              )}
            </p>
          )}
        </div>

        <div className="card shadow-soft p-6 print-hide">
          <h2 className="text-xl">How it looks on site — Beaufort cues</h2>
          <p className="mt-1 text-sm text-muted">
            No anemometer? Judge by what you can see. Most height work gets risky from a fresh breeze (Beaufort
            5) up.
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-[0.74rem] uppercase tracking-wide text-muted">
                  <th className="py-1.5 pr-2 font-semibold">Force</th>
                  <th className="py-1.5 pr-2 font-semibold">mph</th>
                  <th className="py-1.5 pr-2 font-semibold">m/s</th>
                  <th className="py-1.5 font-semibold">What you see</th>
                </tr>
              </thead>
              <tbody>
                {BEAUFORT.map((b) => (
                  <tr key={b.n} className="border-b border-line/60 align-top">
                    <td className="py-2 pr-2 font-semibold text-ink">
                      {b.n} <span className="font-normal text-muted">{b.name}</span>
                    </td>
                    <td className="py-2 pr-2 text-body">{b.mph}</td>
                    <td className="py-2 pr-2 text-body">{b.ms}</td>
                    <td className="py-2 text-body">{b.cue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Printable wind action card */}
      <div className="space-y-4">
        <div className="print-area card shadow-soft p-6" id="wind-action-card">
          <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
            Site wind action card
          </div>
          <h2 className="mt-1 text-lg">Stop-work wind limits by activity</h2>
          <p className="mt-2 text-[0.82rem] leading-relaxed text-muted">
            Commonly used planning figures. The manufacturer&apos;s handbook, the appointed person and the
            temporary works designer always govern — and gusts matter more than the mean.
          </p>

          {forecastMs !== null && (
            <p className="mt-3 rounded-lg border border-line bg-mist/70 px-3 py-2 text-[0.82rem] text-body print-hide">
              Showing against today&apos;s entered wind of {msToMph(forecastMs)} mph ({forecastMs.toFixed(1)}{" "}
              m/s).
            </p>
          )}

          <ul className="mt-4 space-y-3">
            {checked.map(({ activity: a, stop, caution }) => (
              <li
                key={a.key}
                className={`rounded-xl border px-3.5 py-3 ${
                  stop
                    ? "border-risk-veryhigh/50 bg-[#fdecec]"
                    : caution
                      ? "border-risk-medium/50 bg-[#fdf6e3]"
                      : "border-line bg-mist/50"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[0.92rem] font-semibold leading-snug text-ink">{a.name}</span>
                  <span className="shrink-0 text-right text-[0.78rem] font-bold text-ink">
                    {msToMph(a.stopMs)} mph
                    <span className="block font-normal text-muted">
                      {a.stopMs} m/s · B{a.beaufort}
                    </span>
                  </span>
                </div>
                {stop && (
                  <span className="mt-1 inline-block rounded-full bg-risk-veryhigh px-2 py-0.5 text-[0.64rem] font-bold uppercase tracking-wide text-white">
                    Stop — at or over limit
                  </span>
                )}
                {caution && (
                  <span className="mt-1 inline-block rounded-full bg-risk-medium px-2 py-0.5 text-[0.64rem] font-bold uppercase tracking-wide text-white">
                    Caution — monitor closely
                  </span>
                )}
                <p className="mt-1.5 text-[0.84rem] leading-relaxed text-body">{a.action}</p>
                <p className="mt-1 text-[0.78rem] leading-relaxed text-muted">
                  Governed by {a.governs}.
                  {a.note ? ` ${a.note}` : ""}
                </p>
              </li>
            ))}
          </ul>

          <DataReviewedNote date="6 June 2026">
            Figures collated from IPAF (MEWPs), PASMA (towers), NASC scaffold guidance, NFRC (roofing) and HSE
            LOLER/MEWP guidance. They are planning defaults only — the relevant handbook, appointed person or
            temporary works designer sets the actual limit.
          </DataReviewedNote>

          <PrintFooter toolName="wind limits checker" />
        </div>
        <div className="flex flex-wrap justify-end gap-3">
          <CopyButton tool="wind-limits-checker" buildText={buildCopyText} label="Copy wind card" />
          <PrintButton tool="wind-limits-checker" label="Print / save wind card as PDF" />
        </div>
      </div>
    </div>
  );
}
